import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios"
import {getAuthHeader, logout} from "@/data/api/auth"

export const api = axios.create({
  headers: {},
})

export interface ApiResponse<T> {
  ok: boolean;
  payload?: T;
  status?: {
    errorMessage?: string;
  };
  response?: AxiosResponse;
}

api.interceptors.request.use((r: AxiosRequestConfig): AxiosRequestConfig => {
  r.baseURL = "/api/"
  r.headers.Authorization = getAuthHeader()
  return r
})

export async function apiRequest(
  url: string,
  data?: unknown,
  method = "get",
): Promise<ApiResponse<never>> {
  try {
    const response = await (
      method === "post" ? api.post(url, data)
        : method === "put" ? api.put(url, data)
          : method === "delete" ? api.delete(url, data as AxiosRequestConfig)
            : api.get(url, data as AxiosRequestConfig))
    if (response?.status < 400) {
      return {
        ok: !response.data.errorMessage,
        payload: response.data,
      }
    } else {
      return {
        ok: false,
        status: response?.data,
        response,
      }
    }
  } catch (err) {
    console.error("postRequest err:", err)
    return {ok: false, response: (err as AxiosError).response}
  }
}

api.interceptors.response.use((response: AxiosResponse<any>): AxiosResponse<any> => {
  return response
}, (err) => {
  if (err && err.response) {
    if (err.response.status === 401) {
      logout()
    } else if (err.response.status > 499 && err.response.status < 600) {
      if (err.response.data.errorMessage !== undefined) {
        console.error("request failed:", err.response.data.errorMessage)
      } else {
        console.error("request failed", err.response.statusText)
      }
    }
  }
  return Promise.reject(err)
})

export async function getApiStatus(): Promise<any|undefined> {
  const path = "/status"
  try {
    const r = await api.get(path)
    if (r && "version" in r.data) {
      return r.data
    } else {
      console.error(`Failed to read API ${path}. Unexpected response:`, r.data)
    }
  } catch (err) {
    console.warn(`Failed to read API from ${path}: ${err}`)
    return "API unavailable"
  }
}
